import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Base Firestore service class
class FirestoreService {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.collectionRef = collection(db, collectionName);
  }

  // Create a new document
  async create(data) {
    try {
      const docRef = await addDoc(this.collectionRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Created ${this.collectionName}:`, docRef.id);
      return { id: docRef.id, ...data };
    } catch (error) {
      console.error(`Error creating ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Get all documents
  async getAll() {
    try {
      const snapshot = await getDocs(this.collectionRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error getting all ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Get document by ID
  async getById(id) {
    try {
      const docRef = doc(this.collectionRef, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Error getting ${this.collectionName} by ID:`, error);
      throw error;
    }
  }

  // Update document
  async update(id, data) {
    try {
      const docRef = doc(this.collectionRef, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Updated ${this.collectionName}:`, id);
      return { id, ...data };
    } catch (error) {
      console.error(`Error updating ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Delete document
  async delete(id) {
    try {
      const docRef = doc(this.collectionRef, id);
      await deleteDoc(docRef);
      
      console.log(`✅ Deleted ${this.collectionName}:`, id);
      return { id };
    } catch (error) {
      console.error(`Error deleting ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Query documents with where clause
  async getWhere(field, operator, value) {
    try {
      const q = query(this.collectionRef, where(field, operator, value));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error querying ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Query with multiple conditions
  async getWhereMultiple(conditions) {
    try {
      let q = this.collectionRef;
      
      conditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error querying ${this.collectionName} with multiple conditions:`, error);
      throw error;
    }
  }

  // Search documents (text search)
  async search(searchTerm, fields) {
    try {
      const allDocs = await this.getAll();
      const term = searchTerm.toLowerCase();
      
      return allDocs.filter(doc => 
        fields.some(field => {
          const value = doc[field];
          return value && value.toString().toLowerCase().includes(term);
        })
      );
    } catch (error) {
      console.error(`Error searching ${this.collectionName}:`, error);
      throw error;
    }
  }
}

// Create service instances
export const organizationService = new FirestoreService('organizations');
export const locationService = new FirestoreService('locations');
export const buildingService = new FirestoreService('buildings');
export const floorService = new FirestoreService('floors');
export const departmentService = new FirestoreService('departments');
export const roomService = new FirestoreService('rooms');
export const bedService = new FirestoreService('beds');
export const personnelService = new FirestoreService('personnel');
export const equipmentService = new FirestoreService('equipment');
export const biologicalEntityService = new FirestoreService('biological_entities');
export const ipcConfigurationService = new FirestoreService('ipcConfiguration');

// User Management Services - Base service
const baseUserService = new FirestoreService('users');

// Enhanced user service with specific methods
class EnhancedUserService extends FirestoreService {
  constructor() {
    super('users');
  }

  async getByBadgeId(badgeId) {
    return await this.getWhere('badge_id', '==', badgeId);
  }

  async getByEmployeeId(employeeId) {
    return await this.getWhere('employee_id', '==', employeeId);
  }

  async getByEmail(email) {
    return await this.getWhere('email', '==', email);
  }

  async getByDepartment(departmentId) {
    const primary = await this.getWhere('primary_department_id', '==', departmentId);
    const secondary = await this.getWhere('secondary_department_ids', 'array-contains', departmentId);
    return [...primary, ...secondary];
  }

  async searchUsers(searchTerm) {
    return await this.search(searchTerm, [
      'first_name', 'last_name', 'full_name', 'email', 'employee_id', 'badge_id'
    ]);
  }

  async getByUserId(userId) {
    return await this.getWhere('user_id', '==', userId);
  }
}

// Export enhanced user service
export const userService = new EnhancedUserService();

// Other user management services
export const roleService = new FirestoreService('roles');
export const userRoleService = new FirestoreService('user_roles');
export const departmentAssignmentService = new FirestoreService('department_assignments');
export const userStatusService = new FirestoreService('user_status_history');
export const shiftAssignmentService = new FirestoreService('shift_assignments');

// Enhanced user availability service
class EnhancedUserAvailabilityService extends FirestoreService {
  constructor() {
    super('user_availability');
  }

  async getByUserId(userId) {
    return await this.getWhere('user_id', '==', userId);
  }
}

export const userAvailabilityService = new EnhancedUserAvailabilityService();

// Permission Management Services
export const permissionService = new FirestoreService('rolePermissions');
export const auditService = new FirestoreService('auditLogs');

// Enhanced permission service
export const enhancedPermissionService = {
  ...permissionService,
  
  async getByRole(roleKey) {
    return await this.getWhere('role_key', '==', roleKey);
  },
  
  async getWhereMultiple(conditions) {
    try {
      let q = this.collectionRef;
      
      conditions.forEach(condition => {
        q = query(q, where(condition.field, condition.operator, condition.value));
      });
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error querying ${this.collectionName} with multiple conditions:`, error);
      throw error;
    }
  },
  
  async assignPermission(roleKey, permission, granted = true) {
    try {
      // Check if permission already exists
      const existing = await this.getWhereMultiple([
        { field: 'role_key', operator: '==', value: roleKey },
        { field: 'permission', operator: '==', value: permission }
      ]);
      
      if (existing.length > 0) {
        // Update existing permission
        return await this.update(existing[0].id, { 
          granted, 
          updatedAt: serverTimestamp() 
        });
      } else {
        // Create new permission
        return await this.create({
          role_key: roleKey,
          permission: permission,
          granted: granted
        });
      }
    } catch (error) {
      console.error('Error assigning permission:', error);
      throw error;
    }
  }
};

// Enhanced audit service
export const enhancedAuditService = {
  ...auditService,
  
  async getByUserId(userId) {
    return await this.getWhere('user_id', '==', userId);
  },
  
  async getByAction(action) {
    return await this.getWhere('action', '==', action);
  },
  
  async logAction(userId, action, resource, details = {}) {
    return await this.create({
      user_id: userId,
      action: action,
      resource: resource,
      details: details,
      timestamp: serverTimestamp(),
      ip_address: '127.0.0.1', // In production, get real IP
      user_agent: 'ZeroPath System'
    });
  }
};

// ⚠️ PLACEHOLDER WARNING: No sample data migration allowed in production
export async function migrateDataToFirestore() {
  console.warn('⚠️ PLACEHOLDER: Sample data migration not allowed in production');
  console.warn('🔧 CONFIGURATION REQUIRED: Please configure real data through the application interface');
  console.warn('📝 SETUP INCOMPLETE: No mock data will be created');
  
  return {
    success: false,
    message: 'No sample data allowed in production. Configure real data through the application.'
  };
}

export default {
  organizationService,
  locationService,
  buildingService,
  floorService,
  departmentService,
  roomService,
  bedService,
  personnelService,
  equipmentService,
  biologicalEntityService,
  ipcConfigurationService,
  userService,
  roleService,
  userRoleService,
  departmentAssignmentService,
  userStatusService,
  shiftAssignmentService,
  userAvailabilityService,
  permissionService: enhancedPermissionService,
  auditService: enhancedAuditService,
  migrateDataToFirestore
};

